/**
 * Modal density / Bonello-criterion bar chart — mobile's light-themed
 * Canvas2D counterpart to web's
 * `web/src/components/room/charts/modalDensityDraw.ts`. Same geometry
 * (`computeModalDensityGeometry`), different ink — see `./_shared.ts`'s
 * module doc.
 *
 * This module does NOT compute room modes itself — that's rectangular-room
 * eigenmode physics owned by `@roomtreatment/floorplan-core`'s
 * `calculateRoomModes`/`calculateSchroederFrequency` (ported there from
 * web's `acousticsPhysics.ts` for this chart). Callers pass the already
 * enumerated mode frequencies + Schroeder frequency in `opts`.
 */

import type { ChartPad } from '../layout';
import { computeModalDensityGeometry } from '../modalDensity';
import { setupCanvas, drawEmptyMessage, drawLogFreqAxisGrid, drawBars, INK } from './_shared';

export const MODAL_DENSITY_PAD: ChartPad = { t: 14, r: 14, b: 26, l: 24 };

export interface DrawModalDensityOpts {
  modeFreqsHz: number[];
  schroederHz: number | null;
  width: number;
  height: number;
  fMin?: number;
  fMax?: number;
}

export function drawModalDensityCanvas(canvas: HTMLCanvasElement, opts: DrawModalDensityOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computeModalDensityGeometry({
    modeFreqsHz: opts.modeFreqsHz,
    schroederHz: opts.schroederHz,
    width: opts.width,
    height: opts.height,
    pad: MODAL_DENSITY_PAD,
    fMin: opts.fMin,
    fMax: opts.fMax,
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No room dimensions yet');
    return;
  }

  drawLogFreqAxisGrid(ctx, geo.frame, geo.xTicks, geo.yTicks);

  const bars = geo.bands
    .filter((band) => band.count > 0)
    .map((band) => ({
      x0: band.x0,
      x1: band.x1,
      y: band.y,
      color: band.violatesBonello ? INK.warn : INK.good,
    }));
  drawBars(ctx, geo.frame, bars);

  if (geo.schroederX != null) {
    const x = geo.frame.plotX + geo.schroederX;
    ctx.strokeStyle = INK.referenceDashed;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(x, geo.frame.plotY);
    ctx.lineTo(x, geo.frame.plotY + geo.frame.plotH);
    ctx.stroke();
    ctx.setLineDash([]);
  }
}
